import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { NAV, HEADQUARTERS, OFFICES, SERVICES, AUDIENCES } from "../mock";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";
import Logo from "./Logo";

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const DesktopNavItem = ({ item }) => {
  const location = useLocation();
  const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  return (
    <div className="group relative">
      <NavLink
        to={item.path}
        className={`nav-link inline-flex items-center gap-1 px-3 py-2 text-[15px] font-medium transition-colors duration-200 ${
          active ? "text-brand-700" : "text-ink-800 hover:text-brand-700"
        }`}
      >
        {item.label}
        {item.children && <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />}
        <span className={`nav-underline ${active ? "scale-x-100" : ""}`} />
      </NavLink>
      {item.children && (
        <div className="invisible absolute left-0 top-full z-50 pt-3 opacity-0 translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0">
          <div className="min-w-[260px] rounded-2xl border border-brand-100 bg-white p-2 shadow-xl shadow-brand-900/10">
            {item.children.map((c) => (
              <NavLink
                key={c.path}
                to={c.path}
                end
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-2.5 text-sm transition-colors duration-150 ${
                    isActive ? "bg-brand-50 text-brand-800 font-semibold" : "text-ink-700 hover:bg-brand-50 hover:text-brand-800"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* utility bar */}
      <div className="hidden border-b border-brand-100/70 bg-sand-50 text-[13px] md:block">
        <div className="container-x flex h-9 items-center justify-end gap-6">
          <a href={`tel:${HEADQUARTERS.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-1.5 text-ink-700 transition-colors hover:text-brand-700">
            <Phone size={13} /> {HEADQUARTERS.phone}
          </a>
          <a
            href="https://greatnorth.net"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-brand-700 transition-colors hover:text-brand-900"
            data-testid="sister-site-link"
          >
            Great North Property Management <ArrowUpRight size={13} />
          </a>
        </div>
      </div>
      {/* main bar */}
      <div
        className={`border-b transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled ? "border-brand-100 bg-white shadow-sm" : "border-brand-100/60 bg-white"
        }`}
      >
        <div className="container-x flex h-20 items-center justify-between lg:h-24">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <DesktopNavItem key={item.path} item={item} />
            ))}
          </nav>
          <div className="hidden lg:block">
            <Button asChild className="btn-pill bg-brand-600 text-white hover:bg-brand-700" data-testid="header-contact-btn">
              <Link to="/contact-us">
                Contact Us <ArrowRight size={16} className="ml-1.5" />
              </Link>
            </Button>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="rounded-full p-2 text-ink-800 transition-colors hover:bg-brand-50 lg:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
                {open ? <X /> : <Menu />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] overflow-y-auto bg-white p-0">
              <div className="border-b border-brand-100 p-5">
                <Logo />
              </div>
              <div className="flex flex-col p-4">
                {NAV.map((item) => (
                  <div key={item.path} className="mb-2">
                    <Link to={item.path} className="block rounded-lg px-3 py-2 font-serif text-xl text-ink-900">
                      {item.label}
                    </Link>
                    {item.children && (
                      <div className="ml-3 border-l border-brand-100 pl-3">
                        {item.children.map((c) => (
                          <Link key={c.path} to={c.path} className="block rounded-lg px-3 py-1.5 text-sm text-ink-700 hover:text-brand-700">
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Button asChild className="btn-pill mt-4 bg-brand-600 text-white hover:bg-brand-700">
                  <Link to="/contact-us">Contact Us</Link>
                </Button>
                <a href="https://greatnorth.net" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 px-3 text-sm font-medium text-brand-700">
                  Great North Property Management <ArrowUpRight size={13} />
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-ink-900 text-sand-100" data-testid="site-footer">
    <div className="container-x grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-12 lg:py-20">
      <div className="lg:col-span-4">
        <Logo dark />
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-sand-300">
          Behind the scenes, helping nonprofits every day. Financial, administrative and facilities support for nonprofit organizations across New England, backed by over 36 years of Great North experience.
        </p>
        <a
          href="https://greatnorth.net"
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-gold-300 transition-colors hover:text-white"
        >
          A Great North Property Management company <ArrowUpRight size={14} />
        </a>
      </div>
      <div className="lg:col-span-2">
        <h4 className="footer-heading">What We Do</h4>
        <ul className="mt-4 space-y-2.5">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link to={`/what-we-do/${s.slug}`} className="footer-link">{s.title}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-2">
        <h4 className="footer-heading">Company</h4>
        <ul className="mt-4 space-y-2.5">
          <li><Link to="/who-we-are" className="footer-link">Who We Are</Link></li>
          <li><Link to="/who-we-are/leadership" className="footer-link">Leadership</Link></li>
          <li><Link to="/who-we-are/presidents-message" className="footer-link">President's Message</Link></li>
          <li><Link to="/why-we-stand-out" className="footer-link">Why We Stand Out</Link></li>
          {AUDIENCES.map((a) => (
            <li key={a.slug}><Link to={`/who-we-serve/${a.slug}`} className="footer-link">{a.title}</Link></li>
          ))}
          <li><Link to="/nonprofit-review" className="footer-link">Free Nonprofit Review</Link></li>
          <li><Link to="/contact-us" className="footer-link">Contact Us</Link></li>
        </ul>
      </div>
      <div className="lg:col-span-4">
        <h4 className="footer-heading">Headquarters</h4>
        <p className="mt-4 text-sm leading-relaxed text-sand-200">
          {HEADQUARTERS.address[0]}<br />{HEADQUARTERS.address[1]}
        </p>
        <a href={`tel:${HEADQUARTERS.phone.replace(/\D/g, "")}`} className="mt-3 block font-serif text-2xl text-white transition-colors hover:text-gold-300">
          {HEADQUARTERS.phone}
        </a>
        <p className="text-sm text-sand-300">{HEADQUARTERS.tollFree} outside NH</p>
        <h4 className="footer-heading mt-8">Regional Offices</h4>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          {OFFICES.map((o) => (
            <Link key={o.name} to="/contact-us" className="text-sm text-sand-300 transition-colors hover:text-white">
              {o.address[1].split(",")[0]}, {o.state}
            </Link>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="container-x flex flex-col items-start justify-between gap-3 py-6 text-xs text-sand-400 md:flex-row md:items-center">
        <p>&copy; {new Date().getFullYear()} Great North Advantage, Inc. All rights reserved.</p>
        <p>Serving nonprofit organizations in NH, MA, ME &amp; RI</p>
      </div>
    </div>
  </footer>
);

const Layout = () => (
  <div className="min-h-screen bg-sand-50 text-ink-900">
    <ScrollToTop />
    <Header />
    <main id="main">
      <Outlet />
    </main>
    <Footer />
    <Toaster position="top-right" richColors />
  </div>
);

export default Layout;
